/**
 * Venttra - Map-based localization validation
 * Enforces en, de, fr, it keys for translatable content maps.
 */
import { CONTENT_LOCALES } from "../constants";

const REQUIRED_KEYS = new Set(CONTENT_LOCALES);

/** True when value is an object with exactly en, de, fr, it keys. */
export function isValidLocaleMapShape(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== REQUIRED_KEYS.size) return false;
  return keys.every((k) => REQUIRED_KEYS.has(k as (typeof CONTENT_LOCALES)[number]));
}

/**
 * Validate that translatable map fields have exactly en, de, fr, it keys.
 * Call from onPreSave when entering Ready for Publishing / publish queues.
 */
export function validateLocalizationMaps(
  values: Record<string, unknown>,
  mapFieldPaths: string[]
): void {
  for (const path of mapFieldPaths) {
    const value = getNestedValue(values, path);
    if (value === undefined) continue;
    if (!isValidLocaleMapShape(value)) {
      throw new Error(
        `${path} must have exactly the keys: en, de, fr, it. ` +
          "Add or remove keys to match the required localization structure."
      );
    }
  }
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}
